import React, { useEffect, useState } from "react";
import { fetchBgvInProgressCandidates } from "../../services/user/api";
import { Search } from "lucide-react";
import "../../styles/user/bgvInProgress.css";
import { FaEye } from "react-icons/fa";

const BgvInProgressCandidatesPage = ({ onViewReportTwo }) => {

  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);


  const [sortField, setSortField] = useState("createddate");
  const [sortOrder, setSortOrder] = useState("desc");

  const totalPages = Math.ceil(totalElements / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;

  useEffect(() => {
    loadCandidates();
  }, [currentPage, entriesPerPage, search, sortField, sortOrder]);

  const loadCandidates = async () => {
    try {
      setIsLoading(true);

      const res = await fetchBgvInProgressCandidates(
        currentPage - 1,
        entriesPerPage,
        search,
        sortField,
        sortOrder
      );

      const data = res.data.data || {};
      setCandidates(data.content || []);
      setTotalElements(data.totalElements || 0);
      setFetchError("");

    } catch (err) {
      console.error(err);
      setFetchError("Failed to load BGV In Progress candidates.");
      setCandidates([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return " ⇅";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  const formatDate = (date) => {
    if (!date) return "---";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  return (
    <>
      <p className="client-header">BGV In Progress Candidates</p>

      <div className="client-wrapper">

        <div className="clinet-table-controls">

          <div className="entries-select">
            Show{" "}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{" "}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          <table className="candidates-clinet-table">
            <colgroup>
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "200px" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => handleSort("candidateid")}>
                  Candidate ID {renderSortIcon("candidateid")}
                </th>

                <th onClick={() => handleSort("fullname")}>
                  Name {renderSortIcon("fullname")}
                </th>

                <th onClick={() => handleSort("email")}>
                  Email {renderSortIcon("email")}
                </th>

                <th onClick={() => handleSort("mobileNumber")}>
                  Mobile {renderSortIcon("mobileNumber")}
                </th>

                <th onClick={() => handleSort("dateOfBirth")}>
                  DOB {renderSortIcon("dateOfBirth")}
                </th>

                <th onClick={() => handleSort("caseInitiatedDateOnlyAsString")}>
                  Created Date {renderSortIcon("caseInitiatedDateOnlyAsString")}
                </th>

                <th>
                  Created Time
                </th>

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="loading">
                    Loading Candidates...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    {fetchError}
                  </td>
                </tr>
              ) : candidates.length > 0 ? (
                candidates.map((c) => (
                  <tr key={c.candidateid}>
                    <td>{c.candidateid}</td>
                    <td>{c.fullname}</td>
                    <td>{c.email}</td>
                    <td>{c.mobileNumber}</td>
                    <td>{c.dateOfBirth}</td>
                    <td>{formatDate(c.caseInitiatedDateOnly)}</td>
                    <td>{c.caseInitiatedTimeOnly || "N/A"}</td>
                    <td>
                      <button
                        className={`icon-btn ${c.bgcStatus === "PENDING_SUBMISSION"
                            ? "text-gray-400"
                            : "text-indigo-600"
                          }`}
                        disabled={c.bgcStatus === "PENDING_SUBMISSION"}
                        onClick={() => onViewReportTwo(c.candidateid)}
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    No candidates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalElements > 0 && (
            <div className="pagination">
              <div className="pagination-summary">
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + candidates.length, totalElements)} of{" "}
                {totalElements} entries
              </div>

              <div className="pagination-buttons-user">
                <button
                  className="page-btn"
                  onClick={() =>
                    setCurrentPage((p) => Math.max(p - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  ←
                </button>

                <button className="page-btn active">
                  {currentPage}
                </button>

                {currentPage < totalPages && (
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    {currentPage + 1}
                  </button>
                )}


                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  →
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default BgvInProgressCandidatesPage;